import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { Navigate, useNavigate } from "react-router-dom";

import {
  PaymentsSubscriptionCreateAttributes,
  PaymentsSubscriptionUpdateAttributes,
} from "~/api/Api";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useTeam } from "~/contexts/TeamProvider";
import { canMember, extractAuthorizations } from "~/helpers/authorizations";
import { messageFromError } from "~/helpers/messageFromError";
import { tryCatchParsed } from "~/helpers/tryCatch";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import { apiClient } from "../../../api/ApiClient";
import { apiProductClient } from "../../../api/ApiProductClient";
import { Pricing } from "../../../components/pricing/Pricing";
import Spinner from "../../../components/ui/spinner/Spinner";
import styles from "./PricingPage.module.scss";

const PricingPage = () => {
  usePageTitle(PAGE_TITLES.subscriptionPlans);
  const { team, refetchTeam } = useTeam();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isFetching } = useQuery({
    // NOT "subscriptionPlans": that key belongs to SubscriptionPage and
    // holds a different data shape — sharing it left the billing page
    // with a cache entry it couldn't read (empty plan card after subscribe).
    queryKey: ["pricingPlans", team?.id],
    queryFn: async () => {
      const subscription = !team?.subscription
        ? null
        : await apiClient.billing.subscriptionList({
            headers: { "Authorize-Member-Actions": "update" },
          });

      // The backend may not let the team change its plan (e.g. during an
      // Autopto trial): skip the plans, the page sends the user back.
      if (
        subscription &&
        !canMember(extractAuthorizations(subscription.data), "update")
      ) {
        return { canChangePlan: false } as const;
      }

      const products = await apiProductClient.products.productsList();

      return {
        canChangePlan: true,
        products: products?.data,
        subscription: subscription ?? [],
      } as const;
    },
  });

  const { mutate: update, isPending: isPendingUpdateSubscription } =
    useMutation({
      mutationFn: async (data: PaymentsSubscriptionUpdateAttributes) => {
        const { error } = await tryCatchParsed(
          apiClient.billing.subscriptionPartialUpdate(data),
        );

        if (error) {
          enqueueSnackbar(
            `Error updating subscription: ${messageFromError(error)}`,
            { variant: "error", persist: true },
          );

          throw error;
        }
      },
      onSuccess: async () => {
        enqueueSnackbar(`Subscription was successfully updated!`, {
          variant: "success",
        });
        await refetchTeam();
        await queryClient.invalidateQueries({
          queryKey: ["subscriptionPlans", team?.id],
        });
        navigate(routes.billing.subscription());
      },
    });

  const { mutate: subscribe, isPending: isPendingCreateSubscription } =
    useMutation({
      mutationFn: async (data: PaymentsSubscriptionCreateAttributes) => {
        const { error } = await tryCatchParsed(
          apiClient.billing.subscriptionCreate(data),
        );

        if (error) {
          enqueueSnackbar(
            `Error creating subscription: ${messageFromError(error)}`,
            { variant: "error", persist: true },
          );

          throw error;
        }
      },
      onSuccess: async () => {
        enqueueSnackbar(`Subscription was successfully created!`, {
          variant: "success",
        });
        await refetchTeam();
        await queryClient.invalidateQueries({
          queryKey: ["subscriptionPlans"],
        });

        navigate(routes.billing.subscription(), {
          state: { openCheckout: true },
        });
      },
      onError: () => {
        queryClient.invalidateQueries({
          queryKey: ["subscriptionPlans", team?.id],
        });
      },
    });

  const onSubscribe = (
    data: Omit<PaymentsSubscriptionCreateAttributes, "ua">,
  ) => {
    if (team?.subscription) {
      update(data);
    } else {
      subscribe({
        ...data,
        ua: "undefined",
      });
    }
  };

  if (
    isFetching ||
    isPendingCreateSubscription ||
    isPendingUpdateSubscription
  ) {
    return <Spinner />;
  }

  if (data && !data.canChangePlan) {
    return <Navigate to={routes.billing.subscription()} replace />;
  }

  return (
    <div>
      <h1 className={styles.title}>{PAGE_TITLES.subscriptionPlans}</h1>

      <Pricing
        //@ts-expect-error refactor password @TODO: fix types issue
        products={data?.products?.products}
        //@ts-expect-error refactor password @TODO: fix types issue
        subscription={data?.subscription?.data}
        mutate={onSubscribe}
      />
    </div>
  );
};
export default PricingPage;
